import React, { useEffect, useRef, useState } from "react";

interface OptionType {
  value: number | string;
  label: string;
}

interface TableSingleSelectDropdownProps {
  value?: number | string | null;
  options: OptionType[];
  placeholder?: string;
  onChange: (selectedOption?: OptionType) => void;
  disabled?: boolean;
}

const TableSingleSelectDropdown: React.FC<TableSingleSelectDropdownProps> = ({
  value,
  options,
  placeholder = "Select...",
  onChange,
  disabled = false,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((o) => String(o.value) === String(value));

  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchText.toLowerCase()),
  );

  const openDropdown = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();

    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      maxHeight: "220px",
      overflowY: "auto",
      background: "#fff",
      border: "1px solid #dee2e6",
      borderRadius: "4px",
    });

    setSearchText("");
    setOpen(true);

    setTimeout(() => searchRef.current?.focus(), 0);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);

  return (
    <div>
      {/* Trigger span */}
      <div
        ref={triggerRef}
        className="form-control form-control-sm"
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          backgroundColor: disabled ? "#e9ecef" : "#fff",
        }}
        onClick={() => !disabled && openDropdown()}
        title={selectedOption?.label}
      >
        <div className="customArrrrrrow">
          <div className="material-icons material-symbols-outlined multiselect_iconSearchArroe">
            keyboard_arrow_down
          </div>
        </div>
        {selectedOption?.label || (
          <>
            <span style={{ color: "#6c757d" }}>{placeholder}</span>
          </>
        )}
      </div>

      {open && (
        <ul ref={dropdownRef} className="list-group" style={dropdownStyle}>
          <li className="selection_dropdown pt-0 mt-0">
            <div className="input-group">
              <input
                ref={searchRef}
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                autoComplete="off"
                style={{ paddingLeft: "40px" }}
              />
              <span className="material-icons material-symbols-outlined multiselect_iconSearch">
                search
              </span>
              <span className="material-icons material-symbols-outlined multiselect_iconclose">
                close
              </span>
            </div>
          </li>

          {filteredOptions.length === 0 ? (
            <li
              className="list-group-item text-muted"
              style={{ fontSize: "12px" }}
            >
              No results found
            </li>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = String(opt.value) === String(value);

              return (
                <li
                  key={opt.value}
                  className="list-group-item"
                  style={{
                    cursor: "pointer",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "5px 10px",
                    backgroundColor: isSelected ? "#e9f3ff" : "#fff",
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(opt);
                    setOpen(false);
                  }}
                >
                  <input type="radio" checked={isSelected} readOnly />
                  <span>{opt.label}</span>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default TableSingleSelectDropdown;
